// A tab left open across a deploy still asks for the previous build's lazy chunks, so the next
// lazy route fails to load: UI-MYACCOUNT-1P "Importing a module script failed" (the chunk can't be
// fetched) and 5Y / 50 / BE "Importing binding name ... is not found" (it loads but no longer links
// against the new build). Vite wraps every dynamic import in its preload helper, which raises
// `vite:preloadError` for both, whichever error boundary then catches the error - so that event,
// not a route errorElement, is where one reload picks up the new build.

/** Session storage key marking that this tab has already reloaded for a chunk-load failure. */
export const CHUNK_RELOAD_KEY = 'p2g-chunk-load-reload';

const CHUNK_LOAD_MESSAGES = [
  'Importing a module script failed', // Safari, chunk not fetched
  'Importing binding name', // Safari, chunk no longer links against the new build
  'Failed to fetch dynamically imported module', // Chrome
  'error loading dynamically imported module', // Firefox
  'Unable to preload CSS' // Vite, a chunk's stylesheet
];

/** Minimal shape of the pieces of a Sentry event this inspects. */
type FilterableEvent = {
  message?: string;
  exception?: { values?: { value?: string }[] };
};

function isChunkLoadMessage(message: string) {
  return CHUNK_LOAD_MESSAGES.some((m) => message.includes(m));
}

/** Reload once per tab session. Returns false when a reload already happened or can't be guarded. */
function claimReload(): boolean {
  try {
    if (window.sessionStorage.getItem(CHUNK_RELOAD_KEY)) {
      return false;
    }
    window.sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
    return true;
  } catch {
    // Without storage nothing stops a reload loop, so leave the failure to the error page.
    return false;
  }
}

export function installChunkLoadRecovery(target: Window = window, reload: () => void = () => window.location.reload()) {
  let reloading = false;

  const onPreloadError = () => {
    if (reloading || !claimReload()) {
      return;
    }
    reloading = true;
    // The error still reaches the error boundary while the page reloads; Sentry drops it via
    // isRecoveredChunkLoadError.
    reload();
  };

  target.addEventListener('vite:preloadError', onPreloadError);

  return {
    uninstall: () => target.removeEventListener('vite:preloadError', onPreloadError),
    /** True for the chunk-load error this page is reloading to recover from, so it isn't reported. */
    isRecoveredChunkLoadError: (event: FilterableEvent, originalException?: unknown): boolean => {
      if (!reloading) {
        return false;
      }
      const original = originalException instanceof Error ? originalException.message : String(originalException ?? '');
      const message = `${event.message ?? ''} ${event.exception?.values?.[0]?.value ?? ''} ${original}`;
      return isChunkLoadMessage(message);
    }
  };
}
